import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@atlaskit/primitives/box';
import { Stack } from '@atlaskit/primitives/stack';
import { xcss } from '@atlaskit/primitives/xcss';
import Button from '@atlaskit/button/button';
import { AnnouncementData } from '../../data/announcements';

import { messages } from './messages';
import { UFOSegment } from '../../analytics/react-ufo';

const SidebarContainerStyles = xcss({
	position: 'sticky',
	top: 'space.500',
	maxHeight: '100vh',
	overflowY: 'auto',
});

const ButtonWrapperStyles = (isSelected: boolean): ReturnType<typeof xcss> =>
	xcss({
		width: '100%',
		paddingBlock: 'space.150',
		paddingInline: 'space.200',
		backgroundColor: isSelected ? 'color.background.selected' : 'elevation.surface',
		font: 'font.body',
		textAlign: 'left',
		cursor: 'pointer',
		':hover': {
			backgroundColor: isSelected
				? 'color.background.selected.hovered'
				: 'color.background.neutral.hovered',
		},
	});

const CategoriesContainerStyles = xcss({
	maxHeight: 'calc(100vh - 100px)',
	overflowY: 'auto',
});

interface CategorySidebarProps {
	announcements: AnnouncementData[];
	selectedCategory: string;
	onCategorySelect: (category: string) => void;
}

export enum CATEGORIES {
	ALL = 'All',
	AI_SEARCH = 'AI Search',
	CHAT = 'Chat',
	AGENT_STUDIO = 'Agent Studio',
	EXTENSION = 'Extension',
}

const DISPLAYED_CATEGORIES_LABEL = {
	ALL: messages.allCategoryLabel,
	AI_SEARCH: messages.aiSearchCategoryLabel,
	CHAT: messages.chatCategoryLabel,
	AGENT_STUDIO: messages.agentStudioCategoryLabel,
	EXTENSION: messages.extensionCategoryLabel,
};

const convertSpaceToUnderscore = (str: string): string => str.replace(/\s+/g, '_').toUpperCase();

const CategorySidebar: React.FC<CategorySidebarProps> = ({
	announcements,
	selectedCategory,
	onCategorySelect,
}) => {
	const announcementTags = announcements.flatMap((announ) => announ.tags);
	const categories = Array.from(new Set(announcementTags));

	return (
		<Box xcss={SidebarContainerStyles}>
			<Box xcss={CategoriesContainerStyles}>
				<UFOSegment name="category-buttons">
					<Stack space="space.200">
						{categories.map((category, index) => {
							const categoryKey = convertSpaceToUnderscore(category);
							const categoryString =
								DISPLAYED_CATEGORIES_LABEL[categoryKey as keyof typeof DISPLAYED_CATEGORIES_LABEL];
							return (
								<UFOSegment key={index} name="category-button">
									<Box
										onClick={() => onCategorySelect(category)}
										xcss={ButtonWrapperStyles(selectedCategory === category)}
									>
										<Button appearance="subtle" spacing="none">
											<FormattedMessage {...categoryString} />
										</Button>
									</Box>
								</UFOSegment>
							);
						})}
					</Stack>
				</UFOSegment>
			</Box>
		</Box>
	);
};

export default CategorySidebar;
